/**
 * The access token lives only in memory; the refresh token is an HttpOnly
 * cookie the page can't read. Claims are decoded (not verified: the API
 * verifies) to drive the UI: role, staff access, forced password change.
 */
export type Role = "owner" | "admin" | "member";
export type StaffRole = "support" | "operator" | "admin";

export interface Claims {
  sub: string;
  tid: string;
  role: Role;
  exp: number;
  staff?: StaffRole;
  pwd_change?: true;
}

export interface Session {
  accessToken: string;
  claims: Claims;
}

type Listener = (session: Session | null) => void;

let current: Session | null = null;
const listeners = new Set<Listener>();

export function decodeClaims(token: string): Claims | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as Partial<Claims>;
    if (typeof claims.sub !== "string" || typeof claims.tid !== "string") return null;
    return claims as Claims;
  } catch {
    return null;
  }
}

export function getSession(): Session | null {
  return current;
}

export function setSession(accessToken: string): Session | null {
  const claims = decodeClaims(accessToken);
  current = claims ? { accessToken, claims } : null;
  for (const listener of listeners) listener(current);
  return current;
}

export function clearSession(): void {
  current = null;
  for (const listener of listeners) listener(null);
}

export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
